"use client";

import { LocaleMessageType } from "@/types/locale";
import React from "react";
import { ContentLocalizableViewBlock } from "@components/common/content/content-localizable-view-block";
import { DesignSystemFixtureMetadata } from "@features/design-system/design-system-types";
import { FixtureInvariantMeta } from "@features/design-system/ui/fixture/fixture-invariant-meta";
import { FixtureLibsUsed } from "@features/design-system/ui/fixture/fixture-libs-used";
import { FixtureMetadata } from "@features/design-system/ui/fixture/fixture-metadata";
import { FixturePropertyImpactTable } from "@features/design-system/ui/fixture/fixture-property-impact-table";
import {
  FixturePropsTable,
  PropertyDefinition,
} from "@features/design-system/ui/fixture/fixture-props-table";
import { FixtureSection } from "@features/design-system/ui/fixture/fixture-section";
import { FixtureWrapper } from "@features/design-system/ui/fixture/fixture-wrapper";

// Хелпер для локализуемых полей (LocaleMessageType)
const R = (ru: string, en = ru) => ({ ru, en });

// Тестовые данные
const shortTextContent: LocaleMessageType = R(
  "Введение в архитектуру микросервисов",
  "Introduction to Microservices Architecture",
);

const mediumTextContent: LocaleMessageType = R(
  "<p>Этот доклад посвящён основным принципам построения микросервисной архитектуры. Мы рассмотрим паттерны проектирования, стратегии декомпозиции монолитов и лучшие практики DevOps.</p>",
  "<p>This talk covers core principles of microservices architecture. We'll explore design patterns, monolith decomposition strategies, and DevOps best practices.</p>",
);

const longTextContent: LocaleMessageType = R(
  "<p>Микросервисная архитектура стала стандартом де-факто для современных высоконагруженных систем. В этом докладе мы детально разберём ключевые аспекты построения распределённых систем на основе микросервисов.</p><p>Темы для обсуждения:</p><ul><li>Принципы разделения ответственности и границы контекстов (DDD)</li><li>Паттерны коммуникации: синхронная и асинхронная связь, Event-Driven Architecture</li><li>Управление данными: Database per Service, SAGA паттерны, Event Sourcing</li><li>Наблюдаемость и мониторинг распределённых систем: распределённая трассировка, централизованное логирование</li><li>Безопасность: аутентификация и авторизация в распределённых системах</li><li>Стратегии миграции с монолита на микросервисы: Strangler Fig, Branch by Abstraction</li></ul><p>Доклад будет полезен архитекторам, тимлидам и разработчикам, планирующим переход на микросервисную архитектуру или оптимизацию существующих решений.</p>",
  "<p>Microservices architecture has become the de facto standard for modern high-load systems. In this talk, we'll dive deep into key aspects of building distributed systems based on microservices.</p><p>Topics for discussion:</p><ul><li>Separation of concerns and bounded contexts (DDD)</li><li>Communication patterns: synchronous and asynchronous communication, Event-Driven Architecture</li><li>Data management: Database per Service, SAGA patterns, Event Sourcing</li><li>Observability and monitoring of distributed systems: distributed tracing, centralized logging</li><li>Security: authentication and authorization in distributed systems</li><li>Migration strategies from monolith to microservices: Strangler Fig, Branch by Abstraction</li></ul><p>This talk will be useful for architects, tech leads, and developers planning to transition to microservices architecture or optimize existing solutions.</p>",
);

const onlyRussianContent: LocaleMessageType = {
  ru: "Контент доступен только на русском языке",
  en: undefined,
};

const onlyEnglishContent: LocaleMessageType = {
  ru: undefined,
  en: "Content available only in English",
};

const emptyContent: LocaleMessageType = R("", "");

const htmlWithSpecialChars: LocaleMessageType = R(
  '<p>Текст с <strong>жирным</strong>, <em>курсивом</em>, <code>кодом</code> и <a href="#">ссылкой</a>.</p><blockquote>Цитата для примера</blockquote>',
  '<p>Text with <strong>bold</strong>, <em>italic</em>, <code>code</code> and <a href="#">link</a>.</p><blockquote>Quote for example</blockquote>',
);

// Документация свойств публичного API
const generalProps: PropertyDefinition[] = [
  {
    name: "value",
    type: "LocaleMessageType",
    defaultValue: "undefined",
    description:
      "Локализованное содержимое блока. Объект с ключами локалей (ru, en) и строковыми значениями (поддерживается HTML).",
  },
  {
    name: "title",
    type: "string",
    defaultValue: "undefined",
    description: "Заголовок блока контента.",
  },
  {
    name: "description",
    type: "string",
    defaultValue: "undefined",
    description: "Описание или подсказка под заголовком.",
  },
  {
    name: "actions",
    type: "ReactNode",
    defaultValue: "undefined",
    description:
      "Дополнительные действия в заголовке блока (например, кнопка редактирования). Отображаются справа от встроенных действий.",
  },
  {
    name: "useShortMode",
    type: "boolean",
    defaultValue: "false",
    description:
      "Включает режим сокращения длинного контента до shrinkCount символов с кнопкой 'Показать ещё'.",
  },
  {
    name: "shrinkCount",
    type: "number",
    defaultValue: "700",
    description: "Количество символов для отображения в режиме сокращения (useShortMode).",
  },
];

const ContentLocalizableViewBlockFixture = () => (
  <>
    {/* 1. Описание */}
    <FixtureSection title="1. Описание" id="about">
      <div className="space-y-4">
        <p>
          Компонент <strong>ContentLocalizableViewBlock</strong> отображает локализованный текстовый
          контент с возможностью переключения между языками, копирования и сокращения длинного
          текста. Используется в формах просмотра/редактирования активности для отображения
          многоязычных полей (заголовок, описание, план).
        </p>
        <p>
          <strong>Когда использовать:</strong> В разделе Activity/Edit для отображения
          локализованных полей заявки (title, shortDescription, fullDescription, plan и т.д.).
        </p>
        <p>
          <strong>Ключевые особенности:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Многоязычность: автоматический переключатель языка для доступных локалей в данных</li>
          <li>Поддержка HTML-разметки с безопасным рендерингом через dangerouslySetInnerHTML</li>
          <li>Встроенная кнопка копирования контента в буфер обмена</li>
          <li>
            Режим сокращения (useShortMode) для длинных текстов с кнопкой &quot;Показать
            ещё&quot;/&quot;Скрыть&quot;
          </li>
          <li>Защита от пустого контента: скрывает блок, если после санитизации текст пустой</li>
          <li>
            Наследует все возможности базового ContentViewBlock (заголовок, описание, actions)
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 2. Meta */}
    <FixtureSection title="2. Meta" id="meta">
      <FixtureMetadata
        componentName="ContentLocalizableViewBlock"
        sourcePath="src/components/common/content/content-localizable-view-block.tsx"
        domain="Common → Content → Proposals / Activity / Edit"
        dependencies={[
          {
            path: "@components/common/content/content-view-block",
            description: "Базовый контейнер для блоков просмотра контента",
          },
          {
            path: "@components/common/buttons/switch-locale-toggle",
            description: "Переключатель языков",
          },
          {
            path: "@components/ui/custom/copy-button-with-tooltip",
            description: "Кнопка копирования с тултипом",
          },
          {
            path: "@components/ui/button",
            description: "UI-примитив кнопки (shadcn/ui)",
          },
          {
            path: "@lib/i18n/utils",
            description: "Утилита localizeFrom для разрешения локализованных значений",
          },
          {
            path: "@lib/content",
            description: "contentTools.getSanitizedContent для проверки пустого HTML",
          },
          {
            path: "next-intl",
            description: "Хуки useLocale и useTranslations для i18n",
          },
        ]}
        version="1.0.0"
      />
    </FixtureSection>

    {/* 3. Бизнес-контекст */}
    <FixtureSection title="3. Бизнес‑контекст" id="business-context">
      <div className="space-y-4">
        <p>
          <strong>Роли и сценарии использования:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            <strong>Спикер (Activity Edit):</strong> Просмотр локализованных полей своей заявки
            (title, description, plan). Ожидаемая ценность: проверка содержания на обоих языках,
            копирование текста для использования в других формах, быстрый переход к редактированию.
          </li>
          <li>
            <strong>Куратор/Организатор:</strong> Просмотр содержания заявок на предмет полноты и
            качества перевода. Ожидаемая ценность: быстрое переключение между языками, визуальная
            проверка HTML-форматирования, копирование для внешнего использования.
          </li>
          <li>
            <strong>Редактор контента:</strong> Проверка качества локализации перед публикацией.
            Ожидаемая ценность: сравнение версий на разных языках, выявление несоответствий,
            контроль HTML-разметки.
          </li>
          <li>
            <strong>Программный комитет:</strong> Оценка заявки по содержанию. Ожидаемая ценность:
            чтение description и plan на предпочитаемом языке, сокращение длинных текстов для
            быстрого скана.
          </li>
        </ul>
        <p>
          <strong>Ключевые ограничения:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Компонент отображает только те языки, которые реально присутствуют в value
            (LocaleMessageType). Недоступные локали автоматически скрываются из переключателя.
          </li>
          <li>
            По умолчанию выбирается язык системы (useLocale), если он доступен в данных. Это
            обеспечивает консистентность UX с остальным приложением.
          </li>
          <li>
            Режим сокращения (useShortMode) применяется глобально к локализованному тексту, а не к
            каждому языку отдельно. Обрезка происходит по количеству символов (shrinkCount, по
            умолчанию 700).
          </li>
          <li>
            HTML-контент рендерится через dangerouslySetInnerHTML после санитизации
            getSanitizedContent(). Если результат пустой (только теги без текста), блок контента не
            отображается.
          </li>
          <li>
            Кнопка копирования всегда доступна и копирует текущий локализованный текст (без
            HTML-тегов, с санитизацией).
          </li>
          <li>
            Футер с кнопкой &quot;Показать ещё&quot; отображается только при useShortMode = true.
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 4. Пропсы и типы */}
    <FixtureSection title="4. Пропсы и типы" id="props-types">
      <FixturePropsTable properties={generalProps} />
      <div className="mt-4 space-y-3">
        <FixturePropertyImpactTable
          properties={[
            {
              propertyPath: "value",
              type: "LocaleMessageType",
              renderImpact:
                "Определяет контент и доступные языки. Автоматически формирует список локалей для переключателя.",
              missingBehavior:
                "Если undefined или пустой объект, контент не отображается (показывается 'Нет данных').",
            },
            {
              propertyPath: "title",
              type: "string",
              renderImpact: "Заголовок блока в левой части шапки.",
              missingBehavior: "Блок рендерится без заголовка (наследуется от ContentViewBlock).",
            },
            {
              propertyPath: "description",
              type: "string",
              renderImpact: "Описание под заголовком, выделенное серым цветом.",
              missingBehavior: "Описание не отображается.",
            },
            {
              propertyPath: "actions",
              type: "ReactNode",
              renderImpact:
                "Дополнительные действия справа в шапке, после переключателя языка и кнопки копирования.",
              missingBehavior:
                "Отображаются только встроенные действия (переключатель и копирование).",
            },
            {
              propertyPath: "useShortMode",
              type: "boolean",
              renderImpact:
                "Включает/выключает режим сокращения текста с кнопкой в футере. Текст обрезается до shrinkCount символов.",
              missingBehavior: "По умолчанию false, текст отображается полностью.",
            },
            {
              propertyPath: "shrinkCount",
              type: "number",
              renderImpact: "Количество символов в коротком режиме перед добавлением '...'.",
              missingBehavior: "По умолчанию 700 символов.",
            },
          ]}
        />
      </div>
    </FixtureSection>

    {/* 5. Инварианты */}
    <FixtureSection title="5. Инварианты" id="invariants">
      {/* 5.1 Базовый */}
      <FixtureSection title="5.1 Базовый рендеринг" id="invariant-base" variant="inner">
        <FixtureInvariantMeta
          id="base-render"
          name="Стандартное отображение с полными данными"
          goal="Проверка базового рендеринга с локализованным контентом на двух языках."
          preconditions="value содержит текст на русском и английском, title и description заданы."
          inputData={[
            "value: { ru: 'текст', en: 'text' }",
            "title: 'Заголовок'",
            "description: 'Описание'",
            "useShortMode: false",
          ]}
          expectedResult="Отображается блок с заголовком, описанием, переключателем языка (ru/en), кнопкой копирования и текстом. При переключении языка текст меняется."
          edgeCases="Нет."
        />
        <FixturePropsTable
          properties={[
            {
              name: "value",
              type: "LocaleMessageType",
              value: "{ ru: 'Введение...', en: 'Introduction...' }",
              description: "Локализованный текст",
            },
            {
              name: "title",
              type: "string",
              value: '"Заголовок активности"',
              description: "Название блока",
            },
            {
              name: "description",
              type: "string",
              value: '"Краткое описание для подсказки"',
              description: "Вспомогательный текст",
            },
          ]}
        />
        <FixtureWrapper label="Базовый" variant="default">
          <ContentLocalizableViewBlock
            title="Заголовок активности"
            description="Краткое описание для подсказки"
            value={shortTextContent}
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.2 HTML-контент */}
      <FixtureSection title="5.2 HTML-контент" id="invariant-html" variant="inner">
        <FixtureInvariantMeta
          id="html-content"
          name="Отображение HTML-разметки"
          goal="Проверка корректного рендеринга HTML-тегов в локализованном контенте."
          preconditions="value содержит HTML-разметку (параграфы, списки, ссылки)."
          inputData={["value: { ru: '<p>Текст...</p><ul>...</ul>', en: '<p>Text...</p>' }"]}
          expectedResult="HTML корректно рендерится через dangerouslySetInnerHTML. Теги <p>, <ul>, <strong>, <em>, <code>, <a> и <blockquote> отображаются с сохранением форматирования."
          edgeCases="Потенциально опасный HTML (скрипты, iframe) санитизируется на уровне getSanitizedContent."
        />
        <FixtureWrapper label="HTML-контент" variant="default">
          <ContentLocalizableViewBlock title="Описание с HTML" value={htmlWithSpecialChars} />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.3 Режим сокращения */}
      <FixtureSection title="5.3 Режим сокращения" id="invariant-short-mode" variant="inner">
        <FixtureInvariantMeta
          id="short-mode"
          name="Сокращение длинного контента"
          goal="Проверка работы useShortMode для длинных текстов."
          preconditions="value содержит текст длиннее shrinkCount символов, useShortMode = true."
          inputData={["value: { длинный текст > 700 символов }", "useShortMode: true"]}
          expectedResult="Текст обрезается до 700 символов с добавлением '...'. В футере отображается кнопка 'Показать ещё'. При клике текст разворачивается полностью, кнопка меняется на 'Скрыть'."
          edgeCases="HTML-теги учитываются в подсчёте символов. Обрезка может произойти внутри тега, но dangerouslySetInnerHTML корректно обработает незакрытые теги."
        />
        <FixturePropsTable
          properties={[
            {
              name: "useShortMode",
              type: "boolean",
              value: "true",
              description: "Включён режим сокращения",
            },
            {
              name: "shrinkCount",
              type: "number",
              value: "700",
              description: "По умолчанию 700 символов",
            },
          ]}
        />
        <FixtureWrapper label="Короткий режим" variant="default">
          <ContentLocalizableViewBlock
            title="Полное описание (длинное)"
            value={longTextContent}
            useShortMode
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.4 Кастомный shrinkCount */}
      <FixtureSection
        title="5.4 Кастомный shrinkCount"
        id="invariant-custom-shrink"
        variant="inner"
      >
        <FixtureInvariantMeta
          id="custom-shrink-count"
          name="Настройка количества символов для сокращения"
          goal="Проверка кастомного значения shrinkCount."
          preconditions="useShortMode = true, shrinkCount указан явно (например, 200)."
          inputData={["useShortMode: true", "shrinkCount: 200"]}
          expectedResult="Текст обрезается до 200 символов вместо стандартных 700."
          edgeCases="Минимальное значение shrinkCount не ограничено, может быть очень маленьким (например, 50)."
        />
        <FixtureWrapper label="Сокращение до 200 символов" variant="default">
          <ContentLocalizableViewBlock
            title="Краткое описание"
            value={mediumTextContent}
            useShortMode
            shrinkCount={200}
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.5 Только один язык доступен */}
      <FixtureSection title="5.5 Один язык" id="invariant-single-locale" variant="inner">
        <FixtureInvariantMeta
          id="single-locale"
          name="Контент только на одном языке"
          goal="Проверка поведения при наличии контента только на одном языке."
          preconditions="value содержит только ru или только en (второй язык undefined)."
          inputData={["value: { ru: 'текст', en: undefined }"]}
          expectedResult="Переключатель языка отображает только доступный язык (ru). Попытка переключения на en не приводит к ошибке, текст остаётся на ru."
          edgeCases="Если системная локаль (useLocale) не совпадает с доступным языком, компонент должен корректно переключиться на доступный."
        />
        <FixtureWrapper label="Только русский" variant="default">
          <ContentLocalizableViewBlock title="Только на русском" value={onlyRussianContent} />
        </FixtureWrapper>
        <FixtureWrapper label="Только английский" variant="default">
          <ContentLocalizableViewBlock title="Only in English" value={onlyEnglishContent} />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.6 Пустой контент */}
      <FixtureSection title="5.6 Пустой контент" id="invariant-empty" variant="inner">
        <FixtureInvariantMeta
          id="empty-content"
          name="Пустой или отсутствующий контент"
          goal="Проверка защитного рендеринга при пустом value."
          preconditions="value = undefined, пустой объект {} или { ru: '', en: '' }."
          inputData={["value: undefined или { ru: '', en: '' }"]}
          expectedResult="Блок контента не отображается (getSanitizedContent возвращает пустую строку). Рендерится только заголовок и actions без текста."
          edgeCases="Если контент содержит только HTML без текста (например, '<p><br/></p>'), getSanitizedContent также вернёт пустую строку и контент не отобразится."
        />
        <FixtureWrapper label="Пустой контент" variant="default">
          <ContentLocalizableViewBlock title="Без контента" value={emptyContent} />
        </FixtureWrapper>
        <FixtureWrapper label="Undefined value" variant="default">
          <ContentLocalizableViewBlock title="Undefined value" value={undefined} />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.7 С дополнительными actions */}
      <FixtureSection title="5.7 Дополнительные actions" id="invariant-actions" variant="inner">
        <FixtureInvariantMeta
          id="with-actions"
          name="Блок с кастомными действиями"
          goal="Проверка отображения дополнительных действий (например, кнопки редактирования)."
          preconditions="Передан проп actions с ReactNode."
          inputData={["actions: <Button>Edit</Button>"]}
          expectedResult="В шапке блока справа отображаются: переключатель языка, кнопка копирования и кастомные actions (в порядке слева направо)."
          edgeCases="Если actions = null или undefined, отображаются только встроенные действия."
        />
        <FixtureWrapper label="С кнопкой редактирования" variant="default">
          <ContentLocalizableViewBlock
            title="С actions"
            value={shortTextContent}
            actions={
              <button className="rounded bg-blue-500 px-2 py-1 text-xs text-white">Edit</button>
            }
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.8 Галерея всех состояний */}
      <FixtureSection title="5.8 Галерея состояний" id="invariant-gallery" variant="inner">
        <FixtureInvariantMeta
          id="state-gallery"
          name="Сравнение всех основных состояний"
          goal="Визуальная регрессия для различных комбинаций пропсов."
          preconditions="Нет."
          inputData={["Набор блоков с разными комбинациями пропсов"]}
          expectedResult="Отображаются все варианты: базовый, HTML, короткий режим, один язык, пустой, с actions."
          edgeCases="Нет."
        />
        <FixtureWrapper label="Галерея" variant="default">
          <div className="flex flex-col gap-4">
            <ContentLocalizableViewBlock title="Короткий текст" value={shortTextContent} />
            <ContentLocalizableViewBlock title="HTML-контент" value={htmlWithSpecialChars} />
            <ContentLocalizableViewBlock
              title="Длинный текст (сокращённый)"
              value={longTextContent}
              useShortMode
              shrinkCount={300}
            />
            <ContentLocalizableViewBlock title="Только русский" value={onlyRussianContent} />
            <ContentLocalizableViewBlock title="Пустой" value={emptyContent} />
          </div>
        </FixtureWrapper>
      </FixtureSection>
    </FixtureSection>

    {/* 6. Примеры и edge-cases */}
    <FixtureSection title="6. Примеры и крайние случаи" id="edge-cases">
      <div className="space-y-4">
        <p>
          <strong>Полнота данных:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            <strong>Полные данные:</strong> value с ru и en → полноценный переключатель языка и
            контент.
          </li>
          <li>
            <strong>Только один язык:</strong> value с ru или en → переключатель показывает только
            доступный язык.
          </li>
          <li>
            <strong>Пустой контент:</strong> value = {`{}`} или <code>undefined</code> → блок
            рендерится без текста (только заголовок).
          </li>
          <li>
            <strong>Очень длинный текст:</strong> 2000+ символов → useShortMode обрезает до
            shrinkCount с кнопкой.
          </li>
          <li>
            <strong>HTML без текста:</strong>{" "}
            <code>{`value = { ru: '<p><br/></p>', en: '<p></p>' }`}</code> → getSanitizedContent
            вернёт пустую строку, контент не отобразится.
          </li>
        </ul>
        <p>
          <strong>Специальные символы и разметка:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            HTML-теги: <code>&lt;p&gt;</code>, <code>&lt;ul&gt;</code>, <code>&lt;strong&gt;</code>,{" "}
            <code>&lt;em&gt;</code>, <code>&lt;code&gt;</code>, <code>&lt;a&gt;</code>,{" "}
            <code>&lt;blockquote&gt;</code> корректно рендерятся.
          </li>
          <li>Эмодзи в тексте отображаются без проблем.</li>
          <li>HTML-сущности (&amp;nbsp;, &amp;#160;) нормализуются в getSanitizedContent.</li>
          <li>
            Незакрытые HTML-теги могут привести к некорректному отображению (ответственность за
            валидный HTML лежит на входных данных).
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 7. Accessibility */}
    <FixtureSection title="7. Доступность" id="accessibility">
      <ul className="ml-6 list-disc space-y-1">
        <li>
          Переключатель языка доступен с клавиатуры: Tab для фокуса, Enter/Space для переключения.
        </li>
        <li>Кнопка копирования доступна с клавиатуры и показывает тултип при фокусе.</li>
        <li>
          Кнопка &quot;Показать ещё&quot; в футере доступна через Tab, активируется Enter/Space.
        </li>
        <li>
          HTML-контент рендерится с сохранением семантики (ссылки кликабельны, списки имеют
          правильную структуру).
        </li>
        <li>Контрастность текста соответствует WCAG AA.</li>
        <li>
          Атрибут <code>dangerouslySetInnerHTML</code> потенциально опасен, но входные данные
          проходят санитизацию через <code>getSanitizedContent</code>.
        </li>
      </ul>
    </FixtureSection>

    {/* 8. Используемые библиотеки */}
    <FixtureSection title="8. Используемые библиотеки" id="design-system-used">
      <FixtureLibsUsed
        items={[
          { label: "React", href: "https://react.dev/" },
          { label: "Next.js (App Router)", href: "https://nextjs.org/docs/app" },
          { label: "next-intl", href: "https://next-intl-docs.vercel.app/" },
          { label: "shadcn/ui — Button", href: "https://ui.shadcn.com/docs/components/button" },
          { label: "Tailwind CSS", href: "https://tailwindcss.com/docs" },
          { label: "Tabler Icons (@tabler/icons-react)", href: "https://tabler-icons.io/" },
        ]}
      />
    </FixtureSection>

    {/* 9. Рекомендации */}
    <FixtureSection title="9. Рекомендации" id="guidelines">
      <ul className="ml-6 list-disc space-y-1">
        <li>
          Всегда проверяйте, что value содержит хотя бы один язык с непустым текстом, иначе блок
          будет пустым.
        </li>
        <li>
          Используйте useShortMode = true для длинных полей (fullDescription, plan) в карточках и
          списках.
        </li>
        <li>
          Для коротких полей (title, shortDescription) useShortMode не требуется — текст всегда
          виден полностью.
        </li>
        <li>
          HTML-контент должен быть валидным. Незакрытые теги или некорректная разметка могут
          привести к проблемам с отображением.
        </li>
        <li>
          Используйте getSanitizedContent для проверки пустоты HTML перед рендерингом, чтобы
          избежать отображения пустых блоков.
        </li>
        <li>
          Если нужны дополнительные действия (редактирование, удаление), передавайте их через проп
          actions — они будут отображены справа от встроенных действий.
        </li>
      </ul>
    </FixtureSection>

    {/* 10. Бизнес-правила */}
    <FixtureSection title="10. Бизнес‑правила" id="business-rules">
      <div className="space-y-3">
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Инвариант 1 (доступные локали): Переключатель языка отображает только те языки, которые
            реально присутствуют в value. Это достигается через{" "}
            <code>Object.entries(value ?? {}).map(([locale]) =&gt; locale)</code>.
          </li>
          <li>
            Инвариант 2 (системная локаль): По умолчанию выбирается локаль системы (
            <code>useLocale()</code> из next-intl), если она доступна в value. Это обеспечивает
            консистентность с языком интерфейса.
          </li>
          <li>
            Инвариант 3 (режим сокращения): При <code>useShortMode = true</code> текст обрезается до{" "}
            <code>shrinkCount ?? 700</code> символов с добавлением &quot;...&quot;. В футере
            появляется кнопка для переключения между коротким и полным режимом.
          </li>
          <li>
            Инвариант 4 (санитизация): Контент рендерится только если{" "}
            <code>contentTools.getSanitizedContent(text)</code> возвращает непустую строку. Это
            предотвращает отображение пустых HTML-тегов типа{" "}
            <code>&lt;p&gt;&lt;br/&gt;&lt;/p&gt;</code>.
          </li>
          <li>
            Инвариант 5 (HTML-рендеринг): Текст рендерится через{" "}
            <code>dangerouslySetInnerHTML</code> для поддержки HTML-разметки. Это требует валидности
            входных данных.
          </li>
          <li>
            Инвариант 6 (копирование): Кнопка копирования всегда доступна и копирует текущий
            локализованный текст (разрешённый через <code>localizeFrom(value, locale)</code>).
          </li>
          <li>
            Инвариант 7 (футер): Футер с кнопкой отображается только при{" "}
            <code>useShortMode = true</code>. Кнопка переключает состояние <code>shortMode</code> и
            меняет текст между &quot;Показать ещё&quot; и &quot;Скрыть&quot;.
          </li>
          <li>
            Инвариант 8 (наследование): Компонент наследует все возможности{" "}
            <code>ContentViewBlock</code>: заголовок, описание, actions, footer. Это обеспечивает
            единообразие со всеми блоками просмотра контента.
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 11. Примечания/допущения */}
    <FixtureSection title="11. Примечания/допущения" id="notes-assumptions">
      <div className="space-y-3">
        <p>
          <strong>Контекстуальные допущения:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            Компонент использует next-intl для локализации и предполагает, что провайдер настроен на
            уровне приложения.
          </li>
          <li>
            <code>localizeFrom(value, locale)</code> корректно обрабатывает как{" "}
            <code>LocaleMessageType</code>, так и обычные строки. Для <code>LocaleMessageType</code>{" "}
            возвращает <code>value[locale] ?? &quot;&quot;</code>.
          </li>
          <li>
            <code>contentTools.getSanitizedContent()</code> удаляет HTML-теги, нормализует
            HTML-сущности и обрезает пробелы. Если результат пустой, контент не рендерится.
          </li>
          <li>
            Системная локаль (<code>useLocale()</code>) используется как начальное значение для
            переключателя языка. Это обеспечивает консистентность UX с языком интерфейса.
          </li>
          <li>
            Режим сокращения применяется к уже локализованному тексту, а не к каждому языку
            отдельно. Это означает, что обрезка происходит после разрешения локали.
          </li>
          <li>
            <code>dangerouslySetInnerHTML</code> требует доверия к входным данным. В контексте
            Activity/Edit это предполагается безопасным, т.к. контент создаётся самим спикером.
          </li>
        </ul>
        <p>
          <strong>TODO/риски:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Проверить наличие переводов для кнопок &quot;Показать ещё&quot; и &quot;Скрыть&quot; в
            common namespace (words.verbs.show-more, words.verbs.hide).
          </li>
          <li>
            Уточнить политику безопасности для HTML-контента: нужна ли дополнительная санитизация
            (например, через DOMPurify) или текущей достаточно.
          </li>
          <li>
            Протестировать поведение с очень длинными непрерываемыми строками (например, длинные URL
            без пробелов) — могут ломать layout.
          </li>
          <li>
            Подтвердить, что shrinkCount применяется к количеству символов после или до санитизации
            (текущая реализация — до).
          </li>
          <li>
            Рассмотреть возможность добавления индикатора загрузки для асинхронных операций
            копирования (если буфер обмена требует разрешения).
          </li>
        </ul>
      </div>
    </FixtureSection>
  </>
);

export default ContentLocalizableViewBlockFixture;

export const meta: DesignSystemFixtureMetadata = {
  group: "Proposals",
  parentItem: "Activity / Edit",
  title: "Content Localizable View Block",
  description:
    "Локализуемый блок просмотра контента с переключением языков, копированием и сокращением длинного текста",
};
